import { ApolloError } from '@apollo/client';
import {
  getAppSyncErrorInfo,
  getAppSyncErrorType,
} from 'src/api/graphqlErrorType';

/**
 * The exception name every resolver raises for an authorization refusal.
 *
 * A wire value, not display copy: it is the string AppSync puts on the error and
 * the only thing the UI keys its authz treatment on. The message shown to the
 * user always comes from the backend.
 */
export const ACCESS_DENIED_ERROR_TYPE = 'AccessDeniedException';

/**
 * Whether a query/mutation failure is an authorization refusal.
 *
 * Keyed on the errorType, not the message, so wording changes can neither
 * trigger nor defeat the check. Read through `getAppSyncErrorType`, which looks
 * at the TOP-LEVEL `errorType` field AppSync populates — reading
 * `extensions.errorType` directly never matches real traffic.
 *
 * Takes `unknown` because callers hold the failure at different types (an
 * `ApolloError` from a hook, a narrowed `Error | null` in props). Anything
 * without GraphQL errors is not a refusal, so it answers false and the caller's
 * generic error path handles it.
 */
export function isAccessDenied(error: unknown): boolean {
  const graphQLErrors = (error as ApolloError | undefined)?.graphQLErrors;
  return (
    graphQLErrors?.some(
      (e) => getAppSyncErrorType(e) === ACCESS_DENIED_ERROR_TYPE,
    ) ?? false
  );
}

/**
 * The refusal within a failure — the specific GraphQL error `isAccessDenied`
 * matched on, not the `ApolloError` wrapping it.
 *
 * Shared by the readers below so both describe the SAME error. Located
 * independently, a message and a Bindle link could come from two different errors
 * in a multi-error response, and the alert would name a namespace the sentence
 * above it is not about.
 */
function findRefusal(
  error: unknown,
): ApolloError['graphQLErrors'][number] | undefined {
  const graphQLErrors = (error as ApolloError | undefined)?.graphQLErrors;
  return graphQLErrors?.find(
    (e) => getAppSyncErrorType(e) === ACCESS_DENIED_ERROR_TYPE,
  );
}

/**
 * The refusal text to display for a failure `isAccessDenied` matched.
 *
 * Reads the message off the MATCHED error, not the `ApolloError` aggregate:
 * Apollo builds `error.message` by concatenating every GraphQL error in the
 * response, so a partial-data read carrying a refusal alongside an unrelated
 * failure would render both sentences inside the access-denied alert.
 *
 * Returns `''` when nothing matched. Callers guard on `isAccessDenied` first, so
 * that only arises for a refusal whose message the service left blank; each call
 * site has a generic fallback for that.
 */
export function getAccessDeniedMessage(error: unknown): string {
  const refusal = findRefusal(error);
  return refusal?.message ?? '';
}

/**
 * The namespace the caller was refused, paired with the Bindle resource that
 * grants access to it.
 *
 * Both members are non-empty: the resolver drops the detail when either is
 * missing, because one without a `bindleResourceId` would render as a link to a
 * Bindle path with nothing in it — a 404 offered as the route to access.
 */
export interface DeniedNamespace {
  readonly namespaceId: string;
  readonly bindleResourceId: string;
}

/**
 * The namespace a refusal named, with the Bindle resource that grants access to
 * it — the one actionable thing a 403 carries.
 *
 * Read from `errorInfo` on the MATCHED error, for the same reason
 * `getAccessDeniedMessage` reads the message there. The detail is kept OUT of the
 * message by the resolver because the message is displayed verbatim as
 * service-owned prose; the structured half travels separately so the UI can turn
 * it into a link, whose LABEL is UI copy while the refusal sentence is not.
 *
 * Answers `undefined` for every "nothing to offer" case, which is the common one:
 * the field is optional by contract and omitted for search denials (no single
 * namespace), identity-unresolved denials, and namespaces whose Bindle resource
 * could not be looked up. Callers render the message alone then.
 *
 * Re-validated rather than trusted: this is a cross-service payload reaching the
 * UI as `unknown`, and one bad member would put a dead link in front of a user
 * who has no other route to access.
 */
export function getAccessDeniedNamespace(
  error: unknown,
): DeniedNamespace | undefined {
  const raw = getAppSyncErrorInfo(findRefusal(error))?.['deniedNamespace'];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;

  const { namespaceId, bindleResourceId } = raw as Record<string, unknown>;
  if (typeof namespaceId !== 'string' || typeof bindleResourceId !== 'string') {
    return undefined;
  }
  const trimmedNamespaceId = namespaceId.trim();
  const trimmedBindleResourceId = bindleResourceId.trim();
  if (!trimmedNamespaceId || !trimmedBindleResourceId) return undefined;

  return {
    namespaceId: trimmedNamespaceId,
    bindleResourceId: trimmedBindleResourceId,
  };
}
